import React from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentIcon,
  EyeIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import { DocumentTypeGroup } from "./types";
import { DocumentData } from "types";

interface DocumentGroupProps {
  group: DocumentTypeGroup;
  index: number;
  toggleGroupExpansion: (index: number) => void;
  handlePreviewDocument: (doc: DocumentData) => void;
  handleDownloadDocument: (doc: DocumentData) => void;
}

export const DocumentGroup: React.FC<DocumentGroupProps> = ({
  group,
  index,
  toggleGroupExpansion,
  handlePreviewDocument,
  handleDownloadDocument,
}) => {
  const { t } = useTranslation();

  // Helper function to handle pluralization
  const getPluralizedLabel = (baseKey: string, count: number | undefined) => {
    if (count === undefined) return "";
    return count === 1
      ? t(`documents.badges.${baseKey}_one`)
      : t(`documents.badges.${baseKey}_other`);
  };

  // Helper function to get language display name
  const getLanguageDisplayName = (langCode: string): string => {
    return t(`languages.${langCode}`) || langCode;
  };

  // Helper function to get language code tag (2 letters shown in the UI)
  const getLanguageTag = (langCode: string): string => {
    return langCode.toUpperCase();
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Group Header */}
      <button
        type="button"
        aria-expanded={group.expanded}
        className="flex w-full flex-wrap justify-between gap-3 items-center px-4 py-3 bg-white text-left"
        onClick={() => toggleGroupExpansion(index)}
      >
        <div className="flex items-center">
          <DocumentIcon className="h-5 w-5 mr-2 text-gray-500" />
          <div className="font-medium">{group.label}</div>
          <div className="ml-2 text-sm text-gray-500">PDF</div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-gray-100 px-2 py-1 text-sm rounded-full">
            {group.files.length}{" "}
            {getPluralizedLabel("language", group.files.length)}
          </div>
          {group.expanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Group Content */}
      {group.expanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {t("documents.table.language")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {t("documents.table.revision")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {t("documents.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {group.files.map((file, fileIndex) => (
                <tr key={fileIndex}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                        {getLanguageTag(file.lang || "en")}
                      </span>
                      {getLanguageDisplayName(file.lang || "en")}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {file.version || group.version}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => handlePreviewDocument(file)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <EyeIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadDocument(file)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <DownloadIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const DocumentGroupList: React.FC<{
  documentGroups: DocumentTypeGroup[];
  toggleGroupExpansion: (index: number) => void;
  handlePreviewDocument: (doc: DocumentData) => void;
  handleDownloadDocument: (doc: DocumentData) => void;
}> = ({
  documentGroups,
  toggleGroupExpansion,
  handlePreviewDocument,
  handleDownloadDocument,
}) => {
  return (
    <div className="space-y-4">
      {documentGroups.map((group, groupIndex) => (
        <DocumentGroup
          key={groupIndex}
          group={group}
          index={groupIndex}
          toggleGroupExpansion={toggleGroupExpansion}
          handlePreviewDocument={handlePreviewDocument}
          handleDownloadDocument={handleDownloadDocument}
        />
      ))}
    </div>
  );
};
